
import React, { useState } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import ContentGenerator from '@/components/ContentGenerator';
import AssetLibrary from '@/components/AssetLibrary';
import PromptTemplates from '@/components/PromptTemplates';
import ImageEnhancer from '@/components/ImageEnhancer';
import GenerationHistory from '@/components/GenerationHistory';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { 
  Sparkles, 
  Image, 
  Mic, 
  FileText, 
  Crown,
  TrendingUp,
  Calendar,
  Download,
  Zap,
  Target,
  Award,
  ArrowUpRight,
  BarChart3,
  Clock,
  Star,
  Wand2,
  History,
  BookTemplate
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('generate');
  
  // Handle URL parameters for tab switching
  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const tab = urlParams.get('tab');
    if (tab && ['image', 'voice', 'script', 'library', 'enhance', 'templates', 'history'].includes(tab)) {
      // Map URL tabs to dashboard tabs
      if (tab === 'library') {
        setActiveTab('library');
      } else if (tab === 'enhance') {
        setActiveTab('enhance');
      } else if (tab === 'templates') {
        setActiveTab('templates');
      } else if (tab === 'history') {
        setActiveTab('history');
      } else {
        setActiveTab('generate');
        // Set content generator tab if needed with a delay to ensure DOM is ready
        if (['image', 'voice', 'script'].includes(tab)) {
          setTimeout(() => {
            // Trigger the appropriate tab in ContentGenerator
            window.dispatchEvent(new CustomEvent('setContentTab', { detail: { tab } }));
          }, 100);
        }
      }
    }
  }, []);

  const getUsagePercentage = () => {
    if (!user) return 0;
    return Math.round(((user.creditsUsed || 0) / (user.creditsLimit || 1)) * 100);
  };

  const getUsageColor = () => {
    const percentage = getUsagePercentage();
    if (percentage >= 90) return 'bg-red-500';
    if (percentage >= 70) return 'bg-yellow-500';
    return 'bg-primary';
  };

  const getPlanBadgeVariant = () => {
    switch (user?.plan) {
      case 'starter': return 'default';
      case 'pro': return 'secondary';
      case 'team': return 'destructive';
      default: return 'outline';
    }
  };

  const getPlanDisplayName = () => {
    switch (user?.plan) {
      case 'starter': return 'Starter';
      case 'pro': return 'Pro';
      case 'team': return 'Business';
      default: return 'Free';
    }
  };

  return (
    <DashboardLayout>
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20">
        <div className="space-y-8 p-4 sm:p-6 lg:p-8">
          {/* Hero Welcome Section */}
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-primary/10 via-primary/5 to-transparent border border-primary/20 backdrop-blur-sm">
            <div className="absolute inset-0 bg-gradient-to-r from-primary/5 to-transparent" />
            <div className="relative p-6 sm:p-8 lg:p-12">
              <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-3 rounded-2xl bg-primary/10 border border-primary/20">
                      <Sparkles className="w-8 h-8 text-primary" />
                    </div>
                    <Badge variant="secondary" className="px-3 py-1 font-medium">
                      AI Studio
                    </Badge>
                  </div>
                  <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-4">
                    Welcome back, <span className="text-primary">{user?.name?.split(' ')[0] || 'Creator'}</span>!
                  </h1>
                  <p className="text-lg text-muted-foreground mb-6 max-w-2xl">
                    Transform your ideas into stunning visuals, engaging scripts, and realistic voiceovers with our advanced AI tools.
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <Button 
                      size="lg" 
                      onClick={() => setActiveTab('generate')}
                      className="bg-primary hover:bg-primary/90 text-primary-foreground shadow-lg hover:shadow-xl transition-all duration-200"
                    >
                      <Sparkles className="w-5 h-5 mr-2" />
                      Start Creating
                    </Button>
                    <Button 
                      variant="outline" 
                      size="lg"
                      onClick={() => navigate('/dashboard?tab=library')}
                      className="border-border hover:bg-muted/50"
                    >
                      <Download className="w-5 h-5 mr-2" />
                      View Library
                    </Button>
                  </div>
                </div>
                <div className="flex-shrink-0">
                  <div className="relative">
                    <div className="absolute inset-0 bg-primary/20 rounded-full blur-2xl" />
                    <div className="relative w-32 h-32 lg:w-40 lg:h-40 rounded-full bg-gradient-to-br from-primary to-primary/60 flex items-center justify-center">
                      <div className="w-16 h-16 lg:w-20 lg:h-20 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center">
                        <Star className="w-8 h-8 lg:w-10 lg:h-10 text-white" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Current Plan */}
            <Card className="bg-card/50 backdrop-blur-sm border-border/50 hover:shadow-lg transition-all duration-200">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Current Plan</CardTitle>
                <Crown className="h-5 w-5 text-primary" />
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <Badge variant={getPlanBadgeVariant()} className="text-sm font-semibold">
                    {getPlanDisplayName()}
                  </Badge>
                  {user?.plan === 'free' && (
                    <Button size="sm" variant="outline" onClick={() => navigate('/pricing')}>
                      <ArrowUpRight className="w-3 h-3 mr-1" />
                      Upgrade
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Usage Progress */}
            <Card className="bg-card/50 backdrop-blur-sm border-border/50 hover:shadow-lg transition-all duration-200">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Usage This Month</CardTitle>
                <BarChart3 className="h-5 w-5 text-primary" />
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-2xl font-bold text-foreground">
                      {user?.creditsUsed || 0}/{user?.creditsLimit || 0}
                    </span>
                    <span className="text-sm text-muted-foreground">
                      {getUsagePercentage()}%
                    </span>
                  </div>
                  <Progress 
                    value={getUsagePercentage()} 
                    className="h-2"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Quick Actions */}
            <Card className="bg-card/50 backdrop-blur-sm border-border/50 hover:shadow-lg transition-all duration-200">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Quick Actions</CardTitle>
                <Zap className="h-5 w-5 text-primary" />
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-2">
                  <Button size="sm" variant="outline" onClick={() => setActiveTab('generate')}>
                    <Image className="w-3 h-3 mr-1" />
                    Image
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setActiveTab('generate')}>
                    <Mic className="w-3 h-3 mr-1" />
                    Voice
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setActiveTab('generate')}>
                    <FileText className="w-3 h-3 mr-1" />
                    Script
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => navigate('/dashboard?tab=library')}>
                    <Download className="w-3 h-3 mr-1" />
                    Library
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Time Saved */}
            <Card className="bg-card/50 backdrop-blur-sm border-border/50 hover:shadow-lg transition-all duration-200">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Time Saved</CardTitle>
                <Clock className="h-5 w-5 text-primary" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-foreground">
                  {((user?.creditsUsed || 0) * 2.5).toFixed(1)}h
                </div>
                <p className="text-xs text-muted-foreground">
                  Estimated time saved with AI
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Main Content Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-3 sm:grid-cols-5 max-w-4xl mx-auto lg:mx-0 bg-muted/50 backdrop-blur-sm h-auto p-1">
              <TabsTrigger value="generate" className="flex items-center gap-2 font-medium py-2">
                <Sparkles className="w-4 h-4" />
                <span className="hidden sm:inline">Generate</span>
              </TabsTrigger>
              <TabsTrigger value="enhance" className="flex items-center gap-2 font-medium py-2">
                <Wand2 className="w-4 h-4" />
                <span className="hidden sm:inline">Enhance</span>
              </TabsTrigger>
              <TabsTrigger value="templates" className="flex items-center gap-2 font-medium py-2">
                <FileText className="w-4 h-4" />
                <span className="hidden sm:inline">Templates</span>
              </TabsTrigger>
              <TabsTrigger value="history" className="flex items-center gap-2 font-medium py-2">
                <History className="w-4 h-4" />
                <span className="hidden sm:inline">History</span>
              </TabsTrigger>
              <TabsTrigger value="library" className="flex items-center gap-2 font-medium py-2">
                <Download className="w-4 h-4" />
                <span className="hidden sm:inline">Library</span>
              </TabsTrigger>
            </TabsList>

            <TabsContent value="generate" className="mt-8">
              <ContentGenerator />
            </TabsContent>

            <TabsContent value="enhance" className="mt-8">
              <ImageEnhancer />
            </TabsContent>

            <TabsContent value="templates" className="mt-8">
              <PromptTemplates />
            </TabsContent>

            <TabsContent value="history" className="mt-8">
              <GenerationHistory />
            </TabsContent>

            <TabsContent value="library" className="mt-8">
              <AssetLibrary />
            </TabsContent>
          </Tabs>

          {/* AI Features Grid */}
          <div className="mt-12">
            <div className="flex items-center space-x-3 mb-8">
              <div className="p-2 rounded-xl bg-primary/10 border border-primary/20">
                <Award className="w-6 h-6 text-primary" />
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-foreground">AI-Powered Features</h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* AI Images */}
              <Card className="group bg-card/50 backdrop-blur-sm border-border/50 hover:shadow-xl hover:border-primary/30 transition-all duration-300 cursor-pointer" onClick={() => setActiveTab('generate')}>
                <CardHeader>
                  <div className="flex items-center space-x-4">
                    <div className="p-3 rounded-2xl bg-primary/10 border border-primary/20 group-hover:bg-primary/20 transition-colors">
                      <Image className="w-8 h-8 text-primary" />
                    </div>
                    <div>
                      <CardTitle className="text-xl font-semibold text-foreground">AI Images</CardTitle>
                      <Badge variant="secondary" className="mt-1 text-xs">
                        {user?.plan === 'free' ? '512px' : user?.plan === 'starter' ? '1080p' : user?.plan === 'pro' ? '2K' : '4K'} Quality
                      </Badge>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground mb-4">
                    Generate stunning, high-quality images from text descriptions. Multiple styles and resolutions available.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <Badge variant="outline" className="text-xs">Photographic</Badge>
                    <Badge variant="outline" className="text-xs">Digital Art</Badge>
                    <Badge variant="outline" className="text-xs">Fantasy</Badge>
                  </div>
                </CardContent>
              </Card>

              {/* AI Voice */}
              <Card className="group bg-card/50 backdrop-blur-sm border-border/50 hover:shadow-xl hover:border-green-500/30 transition-all duration-300 cursor-pointer" onClick={() => setActiveTab('generate')}>
                <CardHeader>
                  <div className="flex items-center space-x-4">
                    <div className="p-3 rounded-2xl bg-green-500/10 border border-green-500/20 group-hover:bg-green-500/20 transition-colors">
                      <Mic className="w-8 h-8 text-green-500" />
                    </div>
                    <div>
                      <CardTitle className="text-xl font-semibold text-foreground">AI Voice</CardTitle>
                      <Badge variant="secondary" className="mt-1 text-xs">
                        Multilingual
                      </Badge>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground mb-4">
                    Create realistic voiceovers in multiple languages including English, Hindi, Tamil, and more.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <Badge variant="outline" className="text-xs">English</Badge>
                    <Badge variant="outline" className="text-xs">Hindi</Badge>
                    <Badge variant="outline" className="text-xs">Tamil</Badge>
                  </div>
                </CardContent>
              </Card>

              {/* Image Enhancer */}
              <Card className="group bg-card/50 backdrop-blur-sm border-border/50 hover:shadow-xl hover:border-indigo-500/30 transition-all duration-300 cursor-pointer" onClick={() => setActiveTab('enhance')}>
                <CardHeader>
                  <div className="flex items-center space-x-4">
                    <div className="p-3 rounded-2xl bg-indigo-500/10 border border-indigo-500/20 group-hover:bg-indigo-500/20 transition-colors">
                      <Wand2 className="w-8 h-8 text-indigo-500" />
                    </div>
                    <div>
                      <CardTitle className="text-xl font-semibold text-foreground">AI Enhancer</CardTitle>
                      <Badge variant="secondary" className="mt-1 text-xs bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400">
                        NEW
                      </Badge>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground mb-4">
                    Upscale images, remove backgrounds, enhance quality with AI-powered tools.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <Badge variant="outline" className="text-xs">Upscale 4x</Badge>
                    <Badge variant="outline" className="text-xs">BG Remove</Badge>
                    <Badge variant="outline" className="text-xs">Enhance</Badge>
                  </div>
                </CardContent>
              </Card>

              {/* AI Scripts */}
              <Card className="group bg-card/50 backdrop-blur-sm border-border/50 hover:shadow-xl hover:border-orange-500/30 transition-all duration-300 cursor-pointer" onClick={() => setActiveTab('generate')}>
                <CardHeader>
                  <div className="flex items-center space-x-4">
                    <div className="p-3 rounded-2xl bg-orange-500/10 border border-orange-500/20 group-hover:bg-orange-500/20 transition-colors">
                      <FileText className="w-8 h-8 text-orange-500" />
                    </div>
                    <div>
                      <CardTitle className="text-xl font-semibold text-foreground">AI Scripts</CardTitle>
                      <Badge variant="secondary" className="mt-1 text-xs">
                        High Quality
                      </Badge>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground mb-4">
                    Generate engaging scripts, compelling titles, and viral hooks for your content projects.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <Badge variant="outline" className="text-xs">YouTube</Badge>
                    <Badge variant="outline" className="text-xs">Social Media</Badge>
                    <Badge variant="outline" className="text-xs">Marketing</Badge>
                  </div>
                </CardContent>
              </Card>

              {/* Templates Library */}
              <Card className="group bg-card/50 backdrop-blur-sm border-border/50 hover:shadow-xl hover:border-amber-500/30 transition-all duration-300 cursor-pointer" onClick={() => setActiveTab('templates')}>
                <CardHeader>
                  <div className="flex items-center space-x-4">
                    <div className="p-3 rounded-2xl bg-amber-500/10 border border-amber-500/20 group-hover:bg-amber-500/20 transition-colors">
                      <Star className="w-8 h-8 text-amber-500" />
                    </div>
                    <div>
                      <CardTitle className="text-xl font-semibold text-foreground">Templates</CardTitle>
                      <Badge variant="secondary" className="mt-1 text-xs bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                        50+ Prompts
                      </Badge>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground mb-4">
                    Professional prompt templates for images, scripts, and voiceovers.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <Badge variant="outline" className="text-xs">Image Prompts</Badge>
                    <Badge variant="outline" className="text-xs">Scripts</Badge>
                    <Badge variant="outline" className="text-xs">Hooks</Badge>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Dashboard;
